import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { playerAPI } from '../services/api';
import { pickIsoDate, unwrapApiBody } from '../utils/apiResponse';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#E5E7EB', lightGray: '#f3f4f6' };

const SLOT = {
  availBg: '#ffffff',
  availBorder: '#d1d5db',
  availText: '#374151',
  selectedBg: '#fde7e9',
  selectedBorder: '#a81d22',
  selectedText: '#a81d22',
  injuryBg: '#fef2f2',
  injuryBorder: '#fecaca',
  injuryText: '#b91c1c',
};

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: ${(p) => (p.$embedded ? '1rem' : '1.75rem')};
  box-shadow: ${(p) => (p.$embedded ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.06)')};
  border: 1px solid #e5e7eb;
  margin-bottom: ${(p) => (p.$embedded ? '0' : '1.5rem')};
`;

const Intro = styled.div`
  font-size: 0.9375rem;
  color: #4b5563;
  line-height: 1.55;
  margin: 0 0 1.25rem 0;
`;

const PeriodSection = styled.div`
  margin: 0 0 1.25rem 0;
`;

const PeriodHeading = styled.h3`
  margin: 0 0 0.4rem 0;
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const PeriodDescription = styled.p`
  margin: 0 0 0.55rem 0;
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.55;
`;

const PeriodDates = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: #6b7280;
`;

const CalendarToolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const WeekTabsGrow = styled.div`
  flex: 1;
  min-width: min(100%, 240px);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(15, 23, 42, 0.15);
    border-radius: 99px;
  }

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }
`;

const SaveTopWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    align-items: flex-start;
    width: 100%;
  }
`;

const SaveButtonTop = styled.button`
  background: ${BRAND.red};
  color: #fff;
  border: none;
  padding: 0.55rem 1.1rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(213, 43, 30, 0.25);

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const UnsavedHintTop = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
  text-align: right;
  max-width: 220px;

  @media (max-width: 768px) {
    text-align: left;
    max-width: none;
  }
`;

/** Sliding white thumb behind the active week (Performance card toggle style). */
const WeekTabHighlight = styled.div.attrs((p) => {
  const c = Number(p.$count) || 1;
  const raw = Number(p.$activeIdx) || 0;
  const i = Math.min(Math.max(0, raw), Math.max(0, c - 1));
  return {
    style: {
      width: `calc((100% - 8px) / ${c})`,
      left: `calc(4px + ((100% - 8px) * ${i}) / ${c})`,
    },
  };
})`
  position: absolute;
  top: 4px;
  bottom: 4px;
  border-radius: 11px;
  background: linear-gradient(180deg, #ffffff 0%, #f4f7fc 100%);
  box-shadow:
    0 4px 14px rgba(4, 54, 99, 0.16),
    0 1px 3px rgba(15, 23, 42, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(15, 23, 42, 0.04);
  transition:
    left 0.32s cubic-bezier(0.25, 0.9, 0.25, 1),
    width 0.32s cubic-bezier(0.25, 0.9, 0.25, 1),
    box-shadow 0.28s ease;
  pointer-events: none;
  z-index: 0;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const WeekTabs = styled.div`
  display: flex;
  align-items: stretch;
  position: relative;
  flex-wrap: nowrap;
  gap: 0;
  padding: 4px;
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
  border-radius: 14px;
  background: linear-gradient(180deg, #dfe6f0 0%, #d0d8e5 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow:
    inset 0 2px 4px rgba(15, 23, 42, 0.07),
    inset 0 -1px 0 rgba(255, 255, 255, 0.45);
  margin-bottom: 0;

  @media (max-width: 768px) {
    width: max-content;
    min-width: max-content;
  }
`;

const WeekTab = styled.button`
  flex: 1 1 0;
  min-width: 72px;
  position: relative;
  z-index: 1;
  padding: 0.52rem 0.38rem;
  border: none;
  border-radius: 11px;
  cursor: pointer;
  font-size: clamp(0.62rem, 1.35vw, 0.8rem);
  font-weight: ${(p) => (p.$active ? 800 : 600)};
  letter-spacing: -0.02em;
  line-height: 1.25;
  text-align: center;
  background: transparent;
  color: ${(p) => (p.$active ? BRAND.blue : '#5b687e')};
  text-shadow: ${(p) => (p.$active ? 'none' : '0 1px 0 rgba(255, 255, 255, 0.78)')};
  box-shadow: none;
  transform: ${(p) => (p.$active ? 'translateY(-1px)' : 'translateY(0)')};
  transition:
    color 0.28s cubic-bezier(0.25, 0.9, 0.25, 1),
    font-weight 0.22s ease,
    transform 0.28s cubic-bezier(0.25, 0.9, 0.25, 1),
    text-shadow 0.25s ease;

  &:hover {
    color: ${BRAND.blue};
  }

  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: color 0.15s ease, font-weight 0.15s ease;
    transform: none;
  }

  @media (max-width: 768px) {
    flex: 0 0 11.5rem;
    min-width: 11.5rem;
    white-space: nowrap;
    font-size: 0.72rem;
    padding: 0.55rem 0.5rem;
  }
`;

const CalendarFrame = styled.div`
  margin-top: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  background: #f3f4f6;
  -webkit-overflow-scrolling: touch;
  padding: 0.5rem;

  @media (max-width: 768px) {
    overflow-x: hidden;
    padding: 0.65rem;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(56px, 1fr));
  gap: 0.5rem;
  align-items: stretch;
  min-width: max(100%, calc(7 * 56px + 6 * 0.5rem));

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-width: 0;
    gap: 0.65rem;
  }
`;

const DayColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
`;

const DayHeader = styled.div`
  text-align: center;
  padding: 0.65rem 0.35rem;
  background: ${(p) => (p.$injury ? '#fee2e2' : BRAND.blue)};
  border-bottom: 1px solid ${(p) => (p.$injury ? '#fecaca' : 'rgba(255, 255, 255, 0.12)')};
  flex-shrink: 0;
`;

const DayName = styled.div`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: ${(p) => (p.$injury ? '#991b1b' : 'rgba(255, 255, 255, 0.88)')};
  text-transform: uppercase;
  margin-bottom: 0.2rem;
`;

const DayNum = styled.div`
  font-size: 1.1rem;
  font-weight: 800;
  color: ${(p) => (p.$injury ? '#7f1d1d' : '#ffffff')};
  line-height: 1.1;
`;

const SelectDayBtn = styled.button`
  margin-top: 0.4rem;
  width: 100%;
  border-radius: 6px;
  border: 1px solid ${(p) => {
    if (p.$injury) return p.$active ? '#fecaca' : 'rgba(153, 27, 27, 0.35)';
    return p.$active ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.35)';
  }};
  background: ${(p) => {
    if (p.$injury) return p.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.55)';
    return p.$active ? 'rgba(255, 255, 255, 0.96)' : 'rgba(255, 255, 255, 0.14)';
  }};
  color: ${(p) => (p.$injury ? '#7f1d1d' : p.$active ? BRAND.blue : '#ffffff')};
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;
  white-space: normal;
  padding: 0.28rem 0.2rem;
  cursor: pointer;
  line-height: 1.2;

  &:hover:not(:disabled) {
    background: ${(p) => (p.$injury ? '#ffffff' : p.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.24)')};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const DayBody = styled.div`
  padding: 0.4rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  min-height: 0;
`;

const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.28rem;
  align-content: start;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.4rem;
  }
`;

const SlotGroupDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 0.35rem 0;
  flex-shrink: 0;
  width: 100%;
`;

const SlotGroupsWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
`;

const SlotBtn = styled.button`
  width: 100%;
  margin: 0;
  padding: 0.28rem 0.18rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border-style: solid;
  border-width: ${(p) => (p.$selected ? '2px' : '1px')};
  border-color: ${(p) => (p.$selected ? SLOT.selectedBorder : SLOT.availBorder)};
  background: ${(p) => (p.$selected ? SLOT.selectedBg : SLOT.availBg)};
  color: ${(p) => (p.$selected ? SLOT.selectedText : SLOT.availText)};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.42 : 1)};
  font-weight: 800;
  line-height: 1.1;
  font-size: clamp(0.95rem, 0.82rem + 0.85vw, 1.14rem);
  letter-spacing: -0.02em;
  min-height: 0;

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$selected ? SLOT.selectedBorder : BRAND.blue)};
  }

  @media (max-width: 768px) {
    font-size: 0.78rem;
    letter-spacing: 0;
    min-height: 2.15rem;
    padding: 0.35rem 0.15rem;
  }
`;

const BlockedColumn = styled.div`
  flex: 1;
  min-height: 120px;
  margin: 0.15rem 0 0;
  border-radius: 8px;
  background: ${(p) => (p.$injury ? '#fef2f2' : '#f3f4f6')};
  border: 1px solid ${(p) => (p.$injury ? '#fecaca' : '#e5e7eb')};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0.65rem 0.45rem;
  font-size: 0.74rem;
  font-weight: 600;
  color: ${(p) => (p.$injury ? '#b91c1c' : '#64748b')};
  line-height: 1.4;
`;

const DayBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  justify-content: center;
  margin-top: 0.35rem;
`;

const PastBadge = styled.span`
  font-size: 0.62rem;
  font-weight: 600;
  color: ${(p) => (p.$injury ? '#991b1b' : BRAND.blue)};
  background: ${(p) => (p.$injury ? '#fff' : 'rgba(255, 255, 255, 0.92)')};
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
`;

const InjuryBanner = styled.div`
  margin: 0 0 1rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.875rem;
  line-height: 1.45;
`;

const HolidayBanner = styled(InjuryBanner)`
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1e3a5f;
`;

const Muted = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
`;

const PrefsWrap = styled.div`
  margin-bottom: 1.25rem;
  padding: 1rem 1.15rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #fafafa;
`;

const PrefsHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-bottom: 0.65rem;
  padding-bottom: 0.35rem;
  border-bottom: 2px solid ${BRAND.red};
`;

const PrefsTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.25;
  flex-shrink: 0;
`;

const PrefsHint = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0 0 0.85rem 0;
  line-height: 1.45;
`;

const PrefChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const PrefChip = styled.button`
  border-radius: 999px;
  padding: ${(p) => (p.$compact ? '0.3rem 0.55rem' : '0.4rem 0.85rem')};
  font-size: ${(p) => (p.$compact ? '0.72rem' : '0.8125rem')};
  font-weight: 700;
  cursor: pointer;
  border: 2px solid ${(p) => (p.$active ? BRAND.red : '#d1d5db')};
  background: ${(p) => (p.$active ? '#fde7e9' : '#ffffff')};
  color: ${(p) => (p.$active ? '#a81d22' : '#374151')};
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    border-color: ${BRAND.blue};
  }
`;

const DayPrefRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.5rem 0.75rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid #e5e7eb;
  &:last-child {
    border-bottom: none;
  }
`;

const DayPrefLabel = styled.div`
  flex: 0 0 7.25rem;
  font-size: 0.8125rem;
  font-weight: 800;
  color: #374151;
`;

const DayPrefChoices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
  align-items: center;
`;

function parseYmd(dateStr) {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Date(y, mo - 1, d, 12, 0, 0);
}

function formatDotDateShort(dateStr) {
  const [y, mo, d] = String(dateStr).split('-').map(Number);
  if (!y || !mo || !d) return String(dateStr);
  const dd = String(d).padStart(2, '0');
  const mm = String(mo).padStart(2, '0');
  const yy = String(y).slice(-2);
  return `${dd}.${mm}.${yy}`;
}

function formatBracketDateRange(startStr, endStr) {
  return `(${formatDotDateShort(startStr)} → ${formatDotDateShort(endStr)})`;
}

function dayHeaderParts(dateStr) {
  try {
    const dt = parseYmd(dateStr);
    const weekday = dt.toLocaleDateString(undefined, { weekday: 'short' });
    const num = dt.getDate();
    return { weekday: weekday.toUpperCase().slice(0, 3), num };
  } catch (_) {
    return { weekday: '', num: '' };
  }
}

function splitIntoWeeks(days) {
  if (!Array.isArray(days) || days.length === 0) return [];
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

function timeLabelToMinutes(label) {
  const s = String(label).slice(0, 5);
  const parts = s.split(':');
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/** Minimum adjacent gap between consecutive labels (e.g. 30 or 60); fallback 60. */
function inferAdjacentSlotStepMinutes(labels) {
  let step = 60;
  let minAdj = Infinity;
  for (let i = 1; i < labels.length; i += 1) {
    const a = timeLabelToMinutes(labels[i - 1]);
    const b = timeLabelToMinutes(labels[i]);
    if (a != null && b != null && b > a) minAdj = Math.min(minAdj, b - a);
  }
  if (Number.isFinite(minAdj) && minAdj > 0) step = minAdj;
  return step;
}

/** Split template labels into groups when there is a gap larger than one slot step (e.g. lunch break). */
function splitSlotLabelsIntoGroups(labels) {
  if (!Array.isArray(labels) || labels.length === 0) return [];
  const step = inferAdjacentSlotStepMinutes(labels);
  const groups = [];
  let current = [];
  for (let i = 0; i < labels.length; i += 1) {
    const lab = labels[i];
    if (i > 0) {
      const prevM = timeLabelToMinutes(labels[i - 1]);
      const curM = timeLabelToMinutes(lab);
      if (prevM != null && curM != null && curM - prevM > step) {
        groups.push(current);
        current = [];
      }
    }
    current.push(lab);
  }
  if (current.length) groups.push(current);
  return groups;
}

const TIME_PREF_IDS = ['morning', 'noon', 'afternoon', 'evening'];
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const TIME_PREF_SET = new Set(TIME_PREF_IDS);

function sortBucketsForDisplay(raw) {
  const arr = Array.isArray(raw)
    ? raw
    : raw != null && String(raw).trim()
      ? [String(raw).trim().toLowerCase()]
      : [];
  const uniq = [...new Set(arr.map((x) => String(x || '').toLowerCase()).filter((x) => TIME_PREF_SET.has(x)))];
  uniq.sort((a, b) => TIME_PREF_IDS.indexOf(a) - TIME_PREF_IDS.indexOf(b));
  return uniq;
}

function normalizePrefsByDayFromPayloadDay(v) {
  if (v == null) return [];
  if (Array.isArray(v)) return sortBucketsForDisplay(v);
  const s = String(v).trim().toLowerCase();
  return TIME_PREF_SET.has(s) ? [s] : [];
}

function emptyPrefsByDay() {
  return Object.fromEntries(DAY_KEYS.map((k) => [k, []]));
}

function migrateLegacyPrefsToByDay(wd, we) {
  const o = emptyPrefsByDay();
  const firstWd = Array.isArray(wd) && wd.length ? String(wd[0]).trim().toLowerCase() : '';
  const firstWe = Array.isArray(we) && we.length ? String(we[0]).trim().toLowerCase() : '';
  const wdOk = firstWd && TIME_PREF_SET.has(firstWd) ? [firstWd] : [];
  const weOk = firstWe && TIME_PREF_SET.has(firstWe) ? [firstWe] : [];
  for (const k of ['mon', 'tue', 'wed', 'thu', 'fri']) o[k] = [...wdOk];
  o.sat = [...weOk];
  o.sun = [...weOk];
  return o;
}

function dayPrefsSignature(prefs) {
  return DAY_KEYS.map((k) => sortBucketsForDisplay(prefs[k] || []).join('\u0001')).join('\u0002');
}

function applyAvailabilityPeriods(data, injuryUntil, holidayUntil) {
  if (!data?.success) return data;
  const injury = pickIsoDate(injuryUntil);
  const holiday = pickIsoDate(holidayUntil);
  if (!injury && !holiday) return data;
  const today = pickIsoDate(data.today) || new Date().toISOString().slice(0, 10);
  const days = (data.days || []).map((d) => {
    const isInjuryBlocked =
      !!d.is_injury_blocked ||
      (!!injury && !d.is_past && d.date >= today && d.date <= injury);
    const isHolidayBlocked =
      !!d.is_holiday_blocked ||
      (!!holiday && !d.is_past && d.date >= today && d.date <= holiday);
    const periodBlocked = isInjuryBlocked || isHolidayBlocked;
    if (!periodBlocked) return d;
    return {
      ...d,
      is_injury_blocked: isInjuryBlocked,
      is_holiday_blocked: isHolidayBlocked,
      editable: !d.is_past && !periodBlocked,
    };
  });
  return {
    ...data,
    injury_until: injury || data.injury_until || null,
    holiday_until: holiday || data.holiday_until || null,
    days,
  };
}

export default function PlayerAvailabilityPage({ embedded = false }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);  const [saving, setSaving] = useState(false);
  const [payload, setPayload] = useState(null);
  const [activeWeek, setActiveWeek] = useState(0);
  /** dateStr -> Set of HH:MM */
  const [selection, setSelection] = useState(() => new Map());
  /** Mon–Sun: coarse bucket ids (multi-select per day) */
  const [prefsByDay, setPrefsByDay] = useState(() => emptyPrefsByDay());
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fortnightRaw, injuryRaw, holidayRaw] = await Promise.all([
        playerAPI.getAvailabilityFortnight(),
        playerAPI.getInjuryPeriod().catch(() => null),
        playerAPI.getHolidayPeriod().catch(() => null),
      ]);
      const fortnight = unwrapApiBody(fortnightRaw);
      const injury = unwrapApiBody(injuryRaw);
      const holiday = unwrapApiBody(holidayRaw);
      const injuryUntil =
        pickIsoDate(injury?.until_date) || pickIsoDate(fortnight?.injury_until);
      const holidayUntil =
        pickIsoDate(holiday?.until_date) || pickIsoDate(fortnight?.holiday_until);
      const data = applyAvailabilityPeriods(fortnight, injuryUntil, holidayUntil);
      setPayload(data);
      if (data.success && Array.isArray(data.days)) {
        const m = new Map();
        for (const row of data.days) {
          m.set(
            row.date,
            new Set((row.time_slots || []).map((x) => String(x).slice(0, 5)))
          );
        }
        setSelection(m);
      }
      if (data.time_preferences_by_day && typeof data.time_preferences_by_day === 'object') {
        const next = emptyPrefsByDay();
        for (const k of DAY_KEYS) {
          next[k] = normalizePrefsByDayFromPayloadDay(data.time_preferences_by_day[k]);
        }
        setPrefsByDay(next);
      } else {
        const wd = Array.isArray(data.time_preferences_weekday) ? data.time_preferences_weekday : null;
        const we = Array.isArray(data.time_preferences_weekend) ? data.time_preferences_weekend : null;
        const legacy = Array.isArray(data.time_preferences) ? data.time_preferences : [];
        if (wd || we) {
          setPrefsByDay(migrateLegacyPrefsToByDay(wd || [], we || []));
        } else if (legacy.length) {
          const s = legacy.map((x) => String(x));
          setPrefsByDay(migrateLegacyPrefsToByDay(s, s));
        } else {
          setPrefsByDay(emptyPrefsByDay());
        }
      }
    } catch (e) {
      toast.error(t('phase2.playerAvailability.loadError'));
      setPayload({ success: false });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSlot = useCallback((dateStr, label, editable) => {
    if (!editable) return;
    setSelection((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(dateStr) || []);
      const key = String(label).slice(0, 5);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      next.set(dateStr, set);
      return next;
    });
  }, []);

  const toggleWholeDay = useCallback((dateStr, editable, labels) => {
    if (!editable) return;
    const keys = (labels || []).map((x) => String(x).slice(0, 5)).filter(Boolean);
    if (!keys.length) return;
    setSelection((prev) => {
      const next = new Map(prev);
      const current = new Set(next.get(dateStr) || []);
      const allOn = keys.every((k) => current.has(k));
      next.set(dateStr, allOn ? new Set() : new Set(keys));
      return next;
    });
  }, []);

  const clearDayPreference = useCallback((dayKey) => {
    setPrefsByDay((prev) => ({ ...prev, [dayKey]: [] }));
  }, []);

  const toggleDayBucket = useCallback((dayKey, bucketId) => {
    if (!bucketId || !TIME_PREF_SET.has(bucketId)) return;
    setPrefsByDay((prev) => {
      const cur = sortBucketsForDisplay(prev[dayKey] || []);
      const i = cur.indexOf(bucketId);
      if (i >= 0) cur.splice(i, 1);
      else cur.push(bucketId);
      return { ...prev, [dayKey]: sortBucketsForDisplay(cur) };
    });
  }, []);

  const slotLabels = payload?.slot_labels_template || [];

  const slotLabelGroups = useMemo(() => splitSlotLabelsIntoGroups(slotLabels), [slotLabels]);

  const dirtyDays = useMemo(() => {
    if (!payload?.days) return {};
    const out = {};
    for (const d of payload.days) {
      if (!d.editable) continue;
      const a = [...(selection.get(d.date) || [])].sort().join(',');
      const b = [...(d.time_slots || []).map((x) => String(x).slice(0, 5))].sort().join(',');
      if (a !== b) out[d.date] = [...(selection.get(d.date) || [])].sort();
    }
    return out;
  }, [payload, selection]);

  const savedPrefsSnapshot = useMemo(() => {
    if (!payload?.success) return null;
    if (payload.time_preferences_by_day && typeof payload.time_preferences_by_day === 'object') {
      const next = emptyPrefsByDay();
      for (const k of DAY_KEYS) {
        next[k] = normalizePrefsByDayFromPayloadDay(payload.time_preferences_by_day[k]);
      }
      return next;
    }
    const wd = Array.isArray(payload.time_preferences_weekday) ? payload.time_preferences_weekday : null;
    const we = Array.isArray(payload.time_preferences_weekend) ? payload.time_preferences_weekend : null;
    const legacy = Array.isArray(payload.time_preferences) ? payload.time_preferences : [];
    if (wd || we) return migrateLegacyPrefsToByDay(wd || [], we || []);
    if (legacy.length) {
      const s = legacy.map((x) => String(x));
      return migrateLegacyPrefsToByDay(s, s);
    }
    return emptyPrefsByDay();
  }, [payload]);

  const prefsDirty = useMemo(() => {
    if (!savedPrefsSnapshot) return false;
    return dayPrefsSignature(prefsByDay) !== dayPrefsSignature(savedPrefsSnapshot);
  }, [prefsByDay, savedPrefsSnapshot]);

  const hasUnsavedChanges = Object.keys(dirtyDays).length > 0 || prefsDirty;

  const weeks = useMemo(() => splitIntoWeeks(payload?.days || []), [payload?.days]);

  useEffect(() => {
    if (activeWeek >= weeks.length) setActiveWeek(0);
  }, [weeks.length, activeWeek]);

  const handleSave = async () => {
    if (!payload?.success) return;
    const days = {};
    for (const d of payload.days) {
      if (!d.editable) continue;
      days[d.date] = [...(selection.get(d.date) || [])].sort();
    }
    setSaving(true);
    try {
      const time_preferences_by_day = Object.fromEntries(
        DAY_KEYS.map((k) => {
          const arr = sortBucketsForDisplay(prefsByDay[k] || []);
          return [k, arr.length ? arr : null];
        })
      );
      await playerAPI.saveAvailabilityFortnight({
        days,
        time_preferences_by_day,
      });
      toast.success(t('phase2.playerAvailability.saved'));
      queryClient.invalidateQueries(['playerAvailabilityFortnight']);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('phase2.playerAvailability.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Muted>{t('phase2.playerAvailability.loading')}</Muted>;
  }

  if (!payload?.success) {
    return <Muted>{t('phase2.playerAvailability.unavailable')}</Muted>;
  }

  if (payload.enabled === false) {
    return <Muted>{t('phase2.playerAvailability.featureOff')}</Muted>;
  }

  const currentWeekDays = weeks[activeWeek] || [];
  const injuryUntil = payload?.injury_until || null;
  const holidayUntil = payload?.holiday_until || null;

  return (
    <Card $embedded={embedded}>
      {injuryUntil ? (
        <InjuryBanner>
          {t('phase2.playerAvailability.injuryPeriodBanner', { date: injuryUntil })}
        </InjuryBanner>
      ) : null}
      {holidayUntil ? (
        <HolidayBanner>
          {t('phase2.playerAvailability.holidayPeriodBanner', { date: holidayUntil })}
        </HolidayBanner>
      ) : null}
      <Intro>{t('phase2.playerAvailability.introShort')}</Intro>

      <PrefsWrap>
        <PrefsHeaderRow>
          <PrefsTitle>{t('phase2.playerAvailability.generalPrefsByDayTitle')}</PrefsTitle>
        </PrefsHeaderRow>
        <PrefsHint>{t('phase2.playerAvailability.generalPrefsByDayHint')}</PrefsHint>
        {DAY_KEYS.map((dayKey) => (
          <DayPrefRow key={dayKey}>
            <DayPrefLabel>{t(`phase2.playerAvailability.day_${dayKey}`)}</DayPrefLabel>
            <DayPrefChoices role="group" aria-label={t(`phase2.playerAvailability.day_${dayKey}`)}>
              <PrefChip
                type="button"
                $compact
                $active={!(prefsByDay[dayKey] && prefsByDay[dayKey].length)}
                aria-pressed={!(prefsByDay[dayKey] && prefsByDay[dayKey].length)}
                onClick={() => clearDayPreference(dayKey)}
              >
                {t('phase2.playerAvailability.pref_none')}
              </PrefChip>
              {TIME_PREF_IDS.map((id) => (
                <PrefChip
                  key={`${dayKey}-${id}`}
                  type="button"
                  $compact
                  $active={Array.isArray(prefsByDay[dayKey]) && prefsByDay[dayKey].includes(id)}
                  aria-pressed={Boolean(Array.isArray(prefsByDay[dayKey]) && prefsByDay[dayKey].includes(id))}
                  onClick={() => toggleDayBucket(dayKey, id)}
                >
                  {t(`phase2.playerAvailability.pref_${id}`)}
                </PrefChip>
              ))}
            </DayPrefChoices>
          </DayPrefRow>
        ))}
      </PrefsWrap>

      <PeriodSection>
        <PeriodHeading>{t('phase2.playerAvailability.period')}</PeriodHeading>
        <PeriodDescription>{t('phase2.playerAvailability.periodDescription')}</PeriodDescription>
        <PeriodDates>
          {formatBracketDateRange(payload.period_start, payload.period_end)}
        </PeriodDates>
      </PeriodSection>

      <CalendarToolbar>
        <WeekTabsGrow>
          {weeks.length > 1 ? (
            <WeekTabs role="tablist" aria-label={t('phase2.playerAvailability.calendarWeeksAria')}>
              <WeekTabHighlight $count={weeks.length} $activeIdx={activeWeek} aria-hidden />
              {weeks.map((weekDays, idx) => {
                const range =
                  weekDays.length > 0
                    ? formatBracketDateRange(weekDays[0].date, weekDays[weekDays.length - 1].date)
                    : '';
                return (
                  <WeekTab
                    key={idx}
                    type="button"
                    role="tab"
                    aria-selected={activeWeek === idx}
                    $active={activeWeek === idx}
                    onClick={() => setActiveWeek(idx)}
                  >
                    {range}
                  </WeekTab>
                );
              })}
            </WeekTabs>
          ) : null}
        </WeekTabsGrow>
        <SaveTopWrap>
          <SaveButtonTop type="button" onClick={handleSave} disabled={saving}>
            {saving ? t('phase2.playerAvailability.saving') : t('phase2.playerAvailability.save')}
          </SaveButtonTop>
          {hasUnsavedChanges && (
            <UnsavedHintTop>{t('phase2.playerAvailability.unsavedHint')}</UnsavedHintTop>
          )}
        </SaveTopWrap>
      </CalendarToolbar>

      <CalendarFrame>
        <CalendarGrid role="grid">
          {currentWeekDays.map((d) => {
            const { weekday, num } = dayHeaderParts(d.date);
            const injuryDay = !!d.is_injury_blocked;
            const holidayDay = !!d.is_holiday_blocked;
            const periodDay = injuryDay || holidayDay;
            const blockedNoSlots = !d.editable && !d.is_past;
            const daySlotKeys = slotLabels.map((x) => String(x).slice(0, 5)).filter(Boolean);
            const selectedForDay = selection.get(d.date) || new Set();
            const allDaySelected =
              daySlotKeys.length > 0 && daySlotKeys.every((k) => selectedForDay.has(k));

            return (
              <DayColumn key={d.date}>
                <DayHeader $injury={periodDay}>
                  <DayName $injury={periodDay}>{weekday}</DayName>
                  <DayNum $injury={periodDay}>{num}</DayNum>
                  {d.editable ? (
                    <SelectDayBtn
                      type="button"
                      $active={allDaySelected}
                      $injury={periodDay}
                      aria-pressed={allDaySelected}
                      aria-label={
                        allDaySelected
                          ? t('phase2.playerAvailability.unselectWholeDayAria', { day: weekday })
                          : t('phase2.playerAvailability.selectWholeDayAria', { day: weekday })
                      }
                      onClick={() => toggleWholeDay(d.date, d.editable, slotLabels)}
                    >
                      {allDaySelected
                        ? t('phase2.playerAvailability.unselectWholeDay')
                        : t('phase2.playerAvailability.selectWholeDay')}
                    </SelectDayBtn>
                  ) : null}
                  {(d.is_past || !d.editable) && (
                    <DayBadges>
                      {d.is_past && <PastBadge>{t('phase2.playerAvailability.past')}</PastBadge>}
                      {injuryDay && (
                        <PastBadge $injury>{t('phase2.playerAvailability.injuryDayBadge')}</PastBadge>
                      )}
                      {holidayDay && (
                        <PastBadge $injury>{t('phase2.playerAvailability.holidayDayBadge')}</PastBadge>
                      )}
                      {!d.is_past && !d.editable && !periodDay && (
                        <PastBadge>{t('phase2.playerAvailability.locked')}</PastBadge>
                      )}
                    </DayBadges>
                  )}
                </DayHeader>
                <DayBody>
                  {blockedNoSlots ? (
                    <BlockedColumn $injury={periodDay}>
                      {injuryDay
                        ? t('phase2.playerAvailability.injuryDayBlocked')
                        : holidayDay
                          ? t('phase2.playerAvailability.holidayDayBlocked')
                          : t('phase2.playerAvailability.blockedDayMessage')}
                    </BlockedColumn>
                  ) : (
                    <SlotGroupsWrap>
                      {slotLabelGroups.map((group, gi) => (
                        <React.Fragment key={`${d.date}-g-${gi}`}>
                          {gi > 0 ? <SlotGroupDivider aria-hidden /> : null}
                          <SlotGrid>
                            {group.map((label) => {
                              const active = (selection.get(d.date) || new Set()).has(
                                String(label).slice(0, 5)
                              );
                              const disabled = !d.editable;
                              return (
                                <SlotBtn
                                  key={`${d.date}-${label}`}
                                  type="button"
                                  $selected={active}
                                  $disabled={disabled}
                                  disabled={disabled}
                                  onClick={() => toggleSlot(d.date, label, d.editable)}
                                >
                                  {String(label).slice(0, 5)}
                                </SlotBtn>
                              );
                            })}
                          </SlotGrid>
                        </React.Fragment>
                      ))}
                    </SlotGroupsWrap>
                  )}
                </DayBody>
              </DayColumn>
            );
          })}
        </CalendarGrid>
      </CalendarFrame>
    </Card>
  );
}
